import React, { useState, useEffect, useCallback, useMemo } from "react";

import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { BiSave, BiX, BiUser, BiBriefcase, BiFile, BiMap, BiPlus, BiTrash, BiDownload } from 'react-icons/bi';
import { API_BASE_URL } from '../utils/urls.js';
import axiosClient from '../utils/apiClient.js';
import Select from "react-select";

import { use } from 'react';

// --- Static Data Definitions based on SQL ENUMs and Form Fields ---
const COURT_JURISDICTIONS = ['Circuit', 'Family', 'Magistrate', 'Federal', 'Other'];
const SERVICE_TYPES = ['Standard', 'Rush', 'Same-Day', 'Stakeout'];
const PREFERRED_CONTACT_METHODS = ['Email', 'Phone', 'Portal Notification'];
const DOCUMENT_TYPES_TO_BE_SERVED = ['Summons', 'Complaint', 'Subpoena', 'Order', 'Writ of Garnishment', 'Other'];

// Define allowed file types for servee attachments and case files
const ALLOWED_SERVE_FILE_TYPES = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp3,.mp4,.mov,.avi,.wav,.xls,.xlsx,.csv";

// --- Component Helper - Moved outside to prevent re-renders ---
const FormSection = ({ title, icon, children }) => (
    <div className="border border-gray-200 rounded-xl p-6 bg-white">
        <h2 className="text-xl font-extrabold text-blue-800 mb-4 flex items-center space-x-2 border-b pb-2">
            {icon}
            <span>{title}</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {children}
        </div>
    </div>
);

// ----------------------------------------------------------------------
// NEW/MODIFIED: Servee File Management Block
// ----------------------------------------------------------------------
const ServeeFileUploader = ({ servee, index, handleServeeAddFile, handleServeeRemoveFile, isServeeNew }) => {
    const [currentServeeFileUpload, setCurrentServeeFileUpload] = useState({ file: null, title: '' });

    const handleFileUploadTemp = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCurrentServeeFileUpload(prev => ({ ...prev, file: file, title: prev.title || file.name }));
        }
    };

    const handleTitleChangeTemp = (e) => {
        setCurrentServeeFileUpload(prev => ({ ...prev, title: e.target.value }));
    };

    const addFile = () => {
        if (currentServeeFileUpload.file && currentServeeFileUpload.title.trim() !== '') {
            handleServeeAddFile(index, {
                file: currentServeeFileUpload.file,
                title: currentServeeFileUpload.title,
                isNew: true, // Mark this as a new file for upload
            });
            setCurrentServeeFileUpload({ file: null, title: '' });
            document.getElementById(`servee_file_upload_${index}`).value = null; // Reset file input
        } else {
            alert('Please select a file and enter a title for the servee document.');
        }
    };

    // Warn if attempting to upload file to a servee that hasn't been saved (no servee.id)
    const canAddFile = servee.id || isServeeNew;

    return (
        <div className="space-y-4 border border-blue-100 p-4 rounded-lg bg-white mt-4">
            <h4 className="text-md font-bold text-gray-700">Servee Attachments (Proof, Photos, etc.)</h4>

            {/* Display Servee Uploaded Files (Existing & New) */}
            {servee.servee_files && servee.servee_files.length > 0 && (
                <div className="col-span-full pt-2">
                    <label className="block text-xs font-bold text-gray-700 mb-1">Files for {servee.full_name || `Servee ${index + 1}`} ({servee.servee_files.length}):</label>
                    <ul className="text-xs text-gray-700 space-y-1">
                        {servee.servee_files.map((item, fileIndex) => (
                            <li key={item.id || fileIndex} className="flex justify-between items-center bg-gray-100 p-2 rounded-md border border-gray-200">
                                <div className="truncate pr-4 flex items-center">
                                    <span className={`font-semibold ${item.isNew ? 'text-green-700' : 'text-blue-800'}`}>{item.title}</span>
                                    <span className="text-gray-500 ml-2">({item.file_name || item.file?.name})</span>
                                    <span className={`ml-2 text-xs font-bold ${item.isNew ? 'text-green-500' : 'text-gray-500'}`}>
                                        {item.isNew ? ' (NEW - Upload Pending)' : ' (Existing)'}
                                    </span>
                                    {!item.isNew && (
                                        <a href={`${API_BASE_URL}/files/download?path=${item.file_path}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 ml-3">
                                            <BiDownload className="w-4 h-4" />
                                        </a>
                                    )}
                                </div>
                                {/* Use item.id for existing files, otherwise fileIndex for new local files */}
                                <button type="button" onClick={() => handleServeeRemoveFile(index, fileIndex, item.id)} className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2">
                                    <BiTrash className="w-4 h-4" />
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* New File Upload Controls */}
            {canAddFile ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4 border-gray-200">
                    <div className="md:col-span-2">
                        <label htmlFor={`servee_file_title_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Attachment Title</label>
                        <input
                            type="text"
                            id={`servee_file_title_${index}`}
                            name="servee_file_title"
                            placeholder="e.g., Entrance Photo, Proof of Service Image"
                            value={currentServeeFileUpload.title}
                            onChange={handleTitleChangeTemp}
                            className="p-1.5 border rounded-md w-full"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">File Upload</label>
                        <input
                            type="file"
                            id={`servee_file_upload_${index}`}
                            onChange={handleFileUploadTemp}
                            accept={ALLOWED_SERVE_FILE_TYPES}
                            className="block w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 cursor-pointer p-1.5"
                        />
                    </div>
                    <div className="md:col-span-3">
                        <button
                            type="button"
                            onClick={addFile}
                            disabled={!currentServeeFileUpload.file || currentServeeFileUpload.title.trim() === ''}
                            className="flex items-center px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 transition duration-300 disabled:opacity-50"
                        >
                            <BiPlus className="w-4 h-4 mr-1" /> Add Servee File
                        </button>
                    </div>
                </div>
            ) : (
                <p className="text-sm text-yellow-600 mt-2">
                    Note: Servee files can only be added once the case has been created and the Servee data is saved.
                </p>
            )}
        </div>
    );
}

// ----------------------------------------------------------------------
// MODIFIED: Servee Form Block (Include ServeeFileUploader and remove single photo_file)
// ----------------------------------------------------------------------
const ServeeFormBlock = ({ servee, index, handleServeeChange, handleRemoveServee, canRemove, handleServeeAddFile, handleServeeRemoveFile, isServeeNew }) => {
    return (
        <div className="col-span-full border border-gray-300 p-4 rounded-lg space-y-4 bg-gray-50">
            <h3 className="text-lg font-bold text-gray-700">Servee #{index + 1}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Basic Info */}
                <div>
                    <label htmlFor={`full_name_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Name of Person/Entity *</label>
                    <input type="text" id={`full_name_${index}`} name="full_name" value={servee.full_name || ''} onChange={(e) => handleServeeChange(index, e)} required className="p-1.5 border rounded-md w-full" />
                </div>
                <div>
                    <label htmlFor={`aliases_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Aliases / AKA</label>
                    <input type="text" id={`aliases_${index}`} name="aliases" value={servee.aliases || ''} onChange={(e) => handleServeeChange(index, e)} className="p-1.5 border rounded-md w-full" />
                </div>
                {/* Address/Contact Info */}
                <div className="col-span-full">
                    <label htmlFor={`primary_address_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Primary Address *</label>
                    <input type="text" id={`primary_address_${index}`} name="primary_address" value={servee.primary_address || ''} onChange={(e) => handleServeeChange(index, e)} required className="p-1.5 border rounded-md w-full" />
                </div>
                <div>
                    <label htmlFor={`apartment_unit_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Apartment / Unit #</label>
                    <input type="text" id={`apartment_unit_${index}`} name="apartment_unit" value={servee.apartment_unit || ''} onChange={(e) => handleServeeChange(index, e)} className="p-1.5 border rounded-md w-full" />
                </div>
                <div>
                    <label htmlFor={`city_state_zip_${index}`} className="block text-sm font-medium text-gray-700 mb-1">City / State / ZIP</label>
                    <input type="text" id={`city_state_zip_${index}`} name="city_state_zip" value={servee.city_state_zip || ''} onChange={(e) => handleServeeChange(index, e)} className="p-1.5 border rounded-md w-full" />
                </div>
                <div>
                    <label htmlFor={`phone_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Phone (if known)</label>
                    <input type="tel" id={`phone_${index}`} name="phone" value={servee.phone || ''} onChange={(e) => handleServeeChange(index, e)} className="p-1.5 border rounded-md w-full" />
                </div>
                <div>
                    <label htmlFor={`email_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Email (if known)</label>
                    <input type="email" id={`email_${index}`} name="email" value={servee.email || ''} onChange={(e) => handleServeeChange(index, e)} className="p-1.5 border rounded-md w-full" />
                </div>
                <div className="col-span-full">
                    <label htmlFor={`alternate_address_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Alternate Address (Work, Secondary, etc.)</label>
                    <textarea id={`alternate_address_${index}`} name="alternate_address" value={servee.alternate_address || ''} onChange={(e) => handleServeeChange(index, e)} rows="2" className="p-1.5 border rounded-md w-full" />
                </div>
                {/* Descriptive Info */}
                <div className="col-span-full">
                    <label htmlFor={`physical_description_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Physical Description</label>
                    <textarea id={`physical_description_${index}`} name="physical_description" placeholder="Gender, Race, Height, Hair, Tattoos, etc." value={servee.physical_description || ''} onChange={(e) => handleServeeChange(index, e)} rows="3" className="p-1.5 border rounded-md w-full" />
                </div>
                <div className="col-span-full">
                    <label htmlFor={`vehicle_description_${index}`} className="block text-sm font-medium text-gray-700 mb-1">Vehicle Description</label>
                    <input type="text" id={`vehicle_description_${index}`} name="vehicle_description" placeholder="Make/Model/Tag" value={servee.vehicle_description || ''} onChange={(e) => handleServeeChange(index, e)} className="p-1.5 border rounded-md w-full" />
                </div>
            </div>

            {/* NEW Servee File Uploader Component */}
            <ServeeFileUploader
                servee={servee}
                index={index}
                handleServeeAddFile={handleServeeAddFile}
                handleServeeRemoveFile={handleServeeRemoveFile}
                isServeeNew={isServeeNew}
            />

            {canRemove && (
                <button type="button" onClick={() => handleRemoveServee(index)} className="flex items-center text-red-500 hover:text-red-700 text-sm mt-4">
                    <BiTrash className="w-4 h-4 mr-1" /> Remove Servee
                </button>
            )}
        </div>
    );
};

// ----------------------------------------------------------------------
// NEW: Component for Displaying Existing Case Files (Copied from PI form)
// ----------------------------------------------------------------------
const ExistingCaseFiles = ({ files, handleRemoveExistingFile }) => {
    const existingFiles = files.filter(f => !f.isNew);

    if (existingFiles.length === 0) return null;

    return (
        <div className="col-span-full pt-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">Existing Case Files ({existingFiles.length}):</label>
            <ul className="text-sm text-gray-700 space-y-2">
                {existingFiles.map((item) => {
                    // For the PS form, we assume existing general files will be managed via the standard /files/document or /files/media endpoints
                    const deleteType = item.type || (item.file_type?.includes('image') || item.file_type?.includes('video') || item.file_type?.includes('audio') ? 'media' : 'document');

                    return (
                        <li key={item.id} className="flex justify-between items-center bg-gray-50 p-2 rounded-md border border-gray-200">
                            <div className="truncate pr-4 flex items-center">
                                <span className="font-semibold text-blue-800 mr-2">{item.title}</span>
                                <span className="text-gray-500 text-xs">({item.file_name} - {deleteType.toUpperCase()})</span>
                                <a href={`${API_BASE_URL}/files/download?path=${item.file_path}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 ml-3">
                                    <BiDownload className="w-4 h-4" />
                                </a>
                            </div>
                            <button
                                type="button"
                                onClick={() => handleRemoveExistingFile(item.id, deleteType)}
                                className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                            >
                                <BiTrash className="w-5 h-5" />
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

// ----------------------------------------------------------------------
// MAIN COMPONENT: CaseForm
// ----------------------------------------------------------------------
const ClientCaseForm = () => {
    const { id } = useParams();
    const isEditMode = !!id;
    const navigate = useNavigate();
    const PS_API_URL = `${API_BASE_URL}/client/cases`; // Assuming you'll have a dedicated PS API route

    // Servee Template with new 'servee_files' array
    const newServeeTemplate = {
        full_name: '', aliases: '', primary_address: '', apartment_unit: '', city_state_zip: '', alternate_address: '',
        phone: '', email: '', physical_description: '', vehicle_description: '',
        servee_files: [], // Array to hold { file: File, title: string, isNew: bool, id: optional }
    };

    // 1. Core Form State - MODIFIED for multiple assigned agents
    const [formData, setFormData] = useState({
        // ... existing fields
        case_number: '',
        case_name: '',
        case_type: 'Process Service', // Hardcode type for simplicity
        status: 'Open', client_id: '', description: '',
        assigned_to_ids: [], // CHANGED TO ARRAY for multiple select
        due_date: '', county_or_region: '', state_or_province: '', court_jurisdiction: '', service_type: '', client_reference_number: '', number_of_pages: 0,
        client_contact_person: '', client_phone_number: '', client_email_address: '', client_billing_address: '',
        preferred_contact_method: 'Email',
        documents_to_be_served: [], service_instructions: '', special_instructions: '',
        custom_fields: {},
    });

    // 2. Nested Data States
    const [serveeData, setServeeData] = useState([newServeeTemplate]);

    // Files array now holds both new files (file: File, isNew: true) and existing files (id: number, isNew: false)
    const [caseFiles, setCaseFiles] = useState([]);
    const [currentFileUpload, setCurrentFileUpload] = useState({ file: null, title: '' });

    // 3. Lookup Data & UI States
    const [users, setUsers] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitStatus, setSubmitStatus] = useState('');
    const [clientSearch, setClientSearch] = useState("");
    const [ isSaved , setIsSaved ] = useState(false);

    const caseStatuses = ['Open', 'In Progress', 'Pending', 'Closed'];

    const formatRoleName = (roleName = "") => {
        return roleName.replace(/_/g, " ").split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
    };

    const token = localStorage.getItem('token');
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    useEffect(() => {
        if (!formData.client_id) return;

        const selectedClient = clients.find(c => c.id === formData.client_id);
        if (!selectedClient) return;
        
        setFormData(prev => ({
            ...prev,
            client_contact_person:
                selectedClient.contact_person ||
                `${selectedClient.first_name} ${selectedClient.last_name}`,
            client_phone_number: selectedClient.phone_number || "",
            client_email_address: selectedClient.email || "",
            client_billing_address: selectedClient.billing_address || "",
            preferred_contact_method:
                selectedClient.preferred_contact_method || "Email",
        }));
    }, [formData.client_id, clients]);

    useEffect(() => {
        console.log("formData updated", formData);
    }, [formData]);
    // --- API & Deletion Handlers ---

    // Handler for deleting an existing general case file (document/media)
    const handleDeleteCaseFile = async (fileId, fileType) => {
        const confirmDelete = window.confirm(`Are you sure you want to delete this ${fileType} file? This action is permanent.`);
        if (!confirmDelete) return;

        try {
            await axios.delete(`${PS_API_URL}/files/${fileType}/${fileId}`, authHeaders);

            // Remove the file from local state
            setCaseFiles(prev => prev.filter(f => f.id !== fileId));

            alert(`${fileType.toUpperCase()} deleted successfully!`);
        } catch (err) {
            console.error('Error deleting general file:', err);
            alert('Failed to delete general file. Check console for details.');
        }
    }

    // Handler for deleting an existing servee attachment
    const handleDeleteServeeFile = async (fileId) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this servee attachment? This action is permanent.');
        if (!confirmDelete) return;

        try {
            // Use the dedicated servee-files delete endpoint (assuming you implemented it based on the previous step)
            await axios.delete(`${PS_API_URL}/servee-files/${fileId}`, authHeaders);

            // Remove the file from local state
            setServeeData(prev => prev.map(servee => ({
                ...servee,
                servee_files: servee.servee_files.filter(f => f.id !== fileId)
            })));

            alert('Servee Attachment deleted successfully!');
        } catch (err) {
            console.error('Error deleting servee file:', err);
            alert('Failed to delete servee attachment. Check console for details.');
        }
    }



// case limit reached

    // --- Data Fetching ---
    useEffect(() => {
        const fetchData = async () => {
            if (!token) {
                setError('Authentication token not found.');
                setLoading(false);
                return;
            }

            try {
                const [clientsRes] = await Promise.all([
                    // Fetch assignable users (servers/agents)
                    axios.get(`${API_BASE_URL}/clients/all`, authHeaders),
                ]);
                
                console.log("Console", clientsRes.data)
                setClients(Array.isArray(clientsRes.data?.data) ? clientsRes.data.data : []);
                const selectedClient = clientsRes.data.data[0];
                

                                setFormData((prev) => ({
                                    ...prev,
                                    client_id: selectedClient.id,
                                    client_contact_person:
                                        selectedClient.contact_person ||
                                        `${selectedClient.first_name} ${selectedClient.last_name}`,
                                    client_phone_number: selectedClient.phone_number || "",
                                    client_email_address: selectedClient.email || "",
                                    client_billing_address: selectedClient.billing_address || "",
                                    preferred_contact_method:
                                        selectedClient.preferred_contact_method || "Email",
                                }));

                if (isEditMode) {
                    const caseRes = await axios.get(`${PS_API_URL}/${id}`, authHeaders);
                    const data = caseRes.data;
                    console.log("fetched case data", data)

                    // --- Case Files & Media (Existing) ---
                    const existingDocs = (data.documents || []).map(d => ({ ...d, isNew: false, type: 'document', title: d.title || d.file_name }));
                    const existingMedia = (data.media || []).map(m => ({ ...m, isNew: false, type: 'media', title: m.title || m.file_name }));
                    setCaseFiles([...existingDocs, ...existingMedia]);
                    
                    // --- Servee Data with Attachments ---
                    let serveesWithFiles = (data.servees || []).map(s => {
                        const existingServeeFiles = (s.servee_attachments || []).map(f => ({
                            ...f,
                            isNew: false,
                            file_name: f.file_name,
                            title: f.title
                        }));

                        return {
                            ...s,
                            servee_files: existingServeeFiles,
                        };
                    });

                    setServeeData(serveesWithFiles.length > 0 ? serveesWithFiles : [newServeeTemplate]);

                    // --- Handle Assigned Agents (Multiple Select) ---
                    let assignedIds = [];
                    if (data.assigned_to_id) {
                        if (typeof data.assigned_to_id === 'string') {
                            assignedIds = data.assigned_to_id
                                .split(',')
                                .map(idStr => parseInt(idStr.trim(), 10))
                                .filter(id => !isNaN(id));
                        } else if (typeof data.assigned_to_id === 'number') {
                            assignedIds = [data.assigned_to_id];
                        } else if (Array.isArray(data.assigned_to_id)) {
                            assignedIds = data.assigned_to_id;
                        }
                    }
                    console.log("data", data)

                    // Documents to be served
                    let docsToBeServed = [];
                    if (Array.isArray(data.documents_to_be_served)) {
                        docsToBeServed = data.documents_to_be_served;
                    } else if (typeof data.document_served === "string" && data.document_served.trim()) {
                        docsToBeServed = data.document_served.split(",").map((s) => s.trim()).filter(Boolean);
                    } else if (typeof data.documents_to_be_served === "string" && data.documents_to_be_served.trim()) {
                        docsToBeServed = data.documents_to_be_served.split(",").map((s) => s.trim()).filter(Boolean);
                    } else if (data.documents?.length > 0 && data.documents[0].documents_to_be_served) {
                        docsToBeServed = data.documents[0].documents_to_be_served.split(",").map((s) => s.trim()).filter(Boolean);
                    }

                    const serviceInst =
                        data.service_instructions ||
                        (data.documents?.length > 0
                            ? data.documents.find((d) => d.service_instructions)?.service_instructions || ""
                            : "") ||
                        "";

                    const specialInst =
                        data.special_instructions ||
                        (data.documents?.length > 0
                            ? data.documents.find((d) => d.special_instructions)?.special_instructions || ""
                            : "") ||
                        "";

                    // --- Main Form Data ---
                    setFormData(prev => ({
                        ...prev, ...data,
                        due_date: data.due_date ? data.due_date.split('T')[0] : '',
                        documents_to_be_served: docsToBeServed,
                        service_instructions: serviceInst,
                        special_instructions: specialInst,
                        custom_fields: data.custom_fields || {},
                        assigned_to_ids: Array.isArray(data.assigned_to_ids) ? data.assigned_to_ids : (data.assigned_to_id ? data.assigned_to_id.split(',').map(s => parseInt(s.trim(), 10)).filter(id => !isNaN(id)) : []),
                        client_contact_person: data.client_info?.contact_person || '', client_phone_number: data.client_info?.phone_number || '',
                        client_email_address: data.client_info?.email_address || '', client_billing_address: data.client_info?.billing_address || '',
                        preferred_contact_method: data.client_info?.preferred_contact_method || 'Email',
                    }));
                }
            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.response?.data?.message || 'Failed to load case data.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, isEditMode, PS_API_URL, token]);


    // --- Handlers ---
    const handleChange = useCallback((e) => {
        const { name, value, type, options } = e.target;

        // Handle multiple select for assigned_to_ids
        if (name === 'assigned_to_ids' && type === 'select-multiple') {
            const selectedIds = Array.from(options)
                .filter(option => option.selected)
                .map(option => parseInt(option.value, 10));

            setFormData(prev => ({ ...prev, [name]: selectedIds }));
            return;
        }

        setFormData(prev => {
            let newState = { ...prev, [name]: value };

            // Logic to auto-fill client details when 'client_id' changes
            if (name === 'client_id') {
                const selectedClient = clients.find(c => c.id === parseInt(value, 10));

                if (selectedClient) {
                    newState = {
                        ...newState,
                        client_contact_person: selectedClient.contact_person || `${selectedClient.first_name} ${selectedClient.last_name}`,
                        client_phone_number: selectedClient.phone_number || '',
                        client_email_address: selectedClient.email || '',
                        client_billing_address: selectedClient.billing_address || '',
                        preferred_contact_method: selectedClient.preferred_contact_method || 'Email',
                    };
                } else if (value === '') {
                    newState = {
                        ...newState,
                        client_contact_person: '', client_phone_number: '', client_email_address: '',
                        client_billing_address: '', preferred_contact_method: 'Email',
                    };
                }
            }
            return newState;
        });
    }, [clients]);

    const handleCustomFieldChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            custom_fields: {
                ...prev.custom_fields,
                [name]: value,
            },
        }));
    };

    const handleServeeChange = (index, e) => {
        const { name, value } = e.target;
        const newServees = [...serveeData];
        newServees[index][name] = value;
        setServeeData(newServees);
    };

    // Removed single photo_file handler, replaced by ServeeFileUploader logic below

    const handleRemoveServee = (indexToRemove) => {
        setServeeData(prev => prev.filter((_, i) => i !== indexToRemove));
    };

    const handleDocumentTypeChange = (e) => {
        const { value, checked } = e.target;
        setFormData(prev => {
            const currentDocs = prev.documents_to_be_served || [];
            if (checked) {
                return { ...prev, documents_to_be_served: [...currentDocs, value] };
            } else {
                return { ...prev, documents_to_be_served: currentDocs.filter(doc => doc !== value) };
            }
        });
    };

    // --- Servee File Handlers ---
    const handleServeeAddFile = (index, fileObject) => {
        setServeeData(prev => prev.map((servee, i) =>
            i === index ? { ...servee, servee_files: [...servee.servee_files, fileObject] } : servee
        ));
    };

    const handleServeeRemoveFile = (serveeIndex, fileIndex, fileId) => {
        const fileToRemove = serveeData[serveeIndex].servee_files[fileIndex];

        // If it's an existing file, call the API delete function
        if (fileId && !fileToRemove.isNew) {
            handleDeleteServeeFile(fileId);
        }

        // Remove file from local state regardless (if new, it just won't be submitted)
        setServeeData(prev => prev.map((servee, i) =>
            i === serveeIndex
                ? { ...servee, servee_files: servee.servee_files.filter((_, j) => j !== fileIndex) }
                : servee
        ));
    };


    // --- General Case File Handlers (Same logic as PI form) ---
    const handleFileUploadTemp = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCurrentFileUpload(prev => ({ ...prev, file: file, title: prev.title || file.name, isNew: true }));
        }
    };

    const handleTitleChangeTemp = (e) => {
        setCurrentFileUpload(prev => ({ ...prev, title: e.target.value }));
    };

    const handleAddFile = () => {
        if (currentFileUpload.file && currentFileUpload.title.trim() !== '') {
            setCaseFiles(prev => [...prev, currentFileUpload]);
            setCurrentFileUpload({ file: null, title: '' });
            document.getElementById('case_file_upload').value = null;
        } else {
            alert('Please select a file and enter a title.');
        }
    };

    // Handler for removing a NEWLY added file (only removes from local state)
    const handleRemoveNewFile = (indexToRemove) => {
        const newFiles = caseFiles.filter(f => f.isNew);
        if (indexToRemove >= 0 && indexToRemove < newFiles.length) {
            const fileToRemove = newFiles[indexToRemove];

            setCaseFiles(prev => {
                const globalIndex = prev.findIndex(f => f === fileToRemove);
                if (globalIndex > -1) {
                    return prev.filter((_, i) => i !== globalIndex);
                }
                return prev;
            });
        }
    };

    // Handler for removing an EXISTING file (removes from local state and calls API delete)
    const handleRemoveExistingFile = (fileId, fileType) => {
        handleDeleteCaseFile(fileId, fileType);
        // Local state update happens inside handleDeleteCaseFile for simplicity
    };


    // --- Submission (Updated for Multiple Agents and Servee Files) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus('submitting');
        setError(null);
        

        // 1. Prepare Data Payload
        const clientInfoData = {
            client_id: formData.client_id,    // <-- IMPORTANT
            contact_person: formData.client_contact_person,
            phone_number: formData.client_phone_number,
            email_address: formData.client_email_address,
            billing_address: formData.client_billing_address,
            preferred_contact_method: formData.preferred_contact_method,
        };


        // Join assigned_to_ids array into a comma-separated string for the assigned_to_id field
        const assignedToIdString = (formData.assigned_to_ids || []).join(',');

        const submissionData = {
            case_data: {
                ...formData,
                client_id: formData.client_id || null,
                assigned_to_ids: formData.assigned_to_ids || [],
                assigned_to_id: assignedToIdString || null,
                due_date: formData.due_date || null,
                is_pi: false,
                case_type: 'Process Service',
                client_info: clientInfoData,
                documents_to_be_served: (formData.documents_to_be_served || []).join(','),
                service_instructions: formData.service_instructions || '',
                special_instructions: formData.special_instructions || '',
            },
            // Filter out servee files, only send servee data
            servee_data: serveeData.map(({ servee_files, ...rest }) => ({ ...rest })),
            custom_fields: formData.custom_fields,
        };

        let finalCaseId;
        let serveeCreationResponse = []; // To store Servee IDs for file upload

        try {
            // 2. Submit Case Data (Create/Update)
            let response;
            if (isEditMode) {
                const updatePayload = {
                    ...submissionData.case_data,
                    assigned_to_ids: formData.assigned_to_ids || [],
                    assigned_to_id: assignedToIdString || null,
                    documents_to_be_served: (formData.documents_to_be_served || []).join(','),
                    service_instructions: formData.service_instructions || '',
                    special_instructions: formData.special_instructions || '',
                    custom_fields: submissionData.custom_fields,
                    servee_data: submissionData.servee_data,
                };
                response = await axios.put(`${PS_API_URL}/${id}`, updatePayload, authHeaders);
                finalCaseId = id;
                serveeCreationResponse = response.data.servees || [];
            } else {
                response = await axios.post(PS_API_URL, submissionData, authHeaders);
                finalCaseId = response.data.caseId;
                serveeCreationResponse = response.data.servees || []; // Get the newly created servee list with IDs
            }

            // 3. Match newly created/updated servee IDs to local state for file upload
            const finalServeeData = serveeData.map((localServee, index) => {
                // Try to find a matching servee response based on ID (for existing) or index (if new and IDs returned sequentially)
                const matchedServee = serveeCreationResponse.find(s => s.id === localServee.id) || serveeCreationResponse[index];
                return {
                    ...localServee,
                    id: matchedServee?.id || localServee.id // Prioritize ID from response
                };
            }).filter(s => s.id); // Only proceed with servees that have an ID

            // 4. Upload NEW Files
            const uploadPromises = [];
            const uploadConfig = { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } };

            // A. Upload general case files (only those marked as new)
            caseFiles.filter(item => item.isNew).forEach(item => {
                const fd = new FormData();
                fd.append('document', item.file);
                fd.append('document_type', item.title); // Use the custom title
                // Keep the existing case-specific fields for the main document upload route
                fd.append('documents_to_be_served', formData.documents_to_be_served.join(','));
                fd.append('service_instructions', formData.service_instructions);
                fd.append('special_instructions', formData.special_instructions);
                uploadPromises.push(axios.post(`${PS_API_URL}/${finalCaseId}/upload`, fd, uploadConfig));
            });

            // B. Upload servee attachments (only those marked as new)
            finalServeeData.forEach((servee) => {
                servee.servee_files.filter(item => item.isNew).forEach(item => {
                    const fd = new FormData();
                    fd.append('document', item.file);
                    fd.append('document_type', item.title);
                    fd.append('servee_id', servee.id); // Pass the servee ID

                    // Use the dedicated servee-file upload route
                    uploadPromises.push(axios.post(`${PS_API_URL}/${finalCaseId}/upload/servee-file`, fd, uploadConfig));
                })
            });

            await Promise.all(uploadPromises);
            setIsSaved(true)
            setSubmitStatus('success');
            setTimeout(() => navigate('/cases'), 1500);

        } catch (err) {
            setSubmitStatus('error');
            console.error('Submission Error:', err.response?.data || err);
            setError(err.response?.data?.message || 'Submission failed. Please try again.');
        }
    };

    if (loading) return <div className="p-6 text-center">Loading form...</div>;
    if (error && submitStatus !== 'submitting') return <div className="p-6 text-center text-red-500">Error: {error}</div>;

    return (
        <div className="p-1 sm:p-4 lg:p-6 max-w-6xl mx-auto">
            <h1 className="text-4xl font-extrabold text-gray-800 mb-8">{id ? 'Edit Process Service Case' : 'IntelServe™ Digital Process Service Intake'}</h1>
            <form onSubmit={handleSubmit} className="space-y-8">

                                {/* 2. Client Information */}
                <FormSection title="1. Client Information" icon={<BiUser className="w-6 h-6" />}>
                    {/* Searchable Client Selector */}
                    <div className="col-span-full">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Search & Select Client / Firm *
                        </label>

                        <Select
                            options={clients.map((c) => ({
                                value: c.id,
                                label: c.agency
                                    ? `${c.agency} (${c.first_name} ${c.last_name})`
                                    : `${c.first_name} ${c.last_name}`,
                                client: c, // full data for later
                            }))}
                            value={
                                formData.client_id
                                    ? clients
                                        .map((c) => ({
                                            value: c.id,
                                            label: c.agency
                                                ? `${c.agency} (${c.first_name} ${c.last_name})`
                                                : `${c.first_name} ${c.last_name}`,
                                            client: c,
                                        }))
                                        .find((opt) => opt.value === formData.client_id)
                                    : null
                            }
                            onChange={(selected) => {
                                const selectedClient = selected.client;

                                setFormData((prev) => ({
                                    ...prev,
                                    client_id: selected.value,
                                    client_contact_person:
                                        selectedClient.contact_person ||
                                        `${selectedClient.first_name} ${selectedClient.last_name}`,
                                    client_phone_number: selectedClient.phone_number || "",
                                    client_email_address: selectedClient.email || "",
                                    client_billing_address: selectedClient.billing_address || "",
                                    preferred_contact_method:
                                        selectedClient.preferred_contact_method || "Email",
                                }));
                            }}
                            placeholder="Search client or agency..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                            isSearchable
                        />
                    </div>

                    {/* Contact Details */}
                    <div>
                        <label htmlFor="client_contact_person" className="block text-sm font-medium text-gray-700 mb-1">
                            Contact Person *
                        </label>
                        <input
                            type="text"
                            id="client_contact_person"
                            name="client_contact_person"
                            value={formData.client_contact_person}
                            onChange={handleChange}
                            required
                            className="p-1.5 border rounded-md w-full"
                        />
                    </div>

                    <div>
                        <label htmlFor="client_phone_number" className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number *
                        </label>
                        <input
                            type="tel"
                            id="client_phone_number"
                            name="client_phone_number"
                            value={formData.client_phone_number}
                            onChange={handleChange}
                            required
                            className="p-1.5 border rounded-md w-full"
                        />
                    </div>

                    <div>
                        <label htmlFor="client_email_address" className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address *
                        </label>
                        <input
                            type="email"
                            id="client_email_address"
                            name="client_email_address"
                            value={formData.client_email_address}
                            onChange={handleChange}
                            required
                            className="p-1.5 border rounded-md w-full"
                        />
                    </div>

                    <div>
                        <label htmlFor="client_billing_address" className="block text-sm font-medium text-gray-700 mb-1">
                            Billing Address
                        </label>
                        <input
                            type="text"
                            id="client_billing_address"
                            name="client_billing_address"
                            value={formData.client_billing_address}
                            onChange={handleChange}
                            className="p-1.5 border rounded-md w-full"
                        />
                    </div>

                    <div>
                        <label htmlFor="preferred_contact_method" className="block text-sm font-medium text-gray-700 mb-1">
                            Preferred Contact Method *
                        </label>
                        <select
                            id="preferred_contact_method"
                            name="preferred_contact_method"
                            value={formData.preferred_contact_method}
                            onChange={handleChange}
                            required
                            className="p-1.5 border rounded-md bg-gray-50 w-full"
                        >
                            <option value="">Select...</option>
                            {PREFERRED_CONTACT_METHODS.map(m => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>
                    </div>
                </FormSection>

                {/* 1. Case Information */}
                <FormSection title="2. Case Information" icon={<BiBriefcase className="w-6 h-6" />}>
                    <div>
                        <label htmlFor="case_name" className="block text-sm font-medium text-gray-700 mb-1">Case Title *</label>
                        <input type="text" id="case_name" name="case_name" placeholder="e.g. Smith v. Johnson" value={formData.case_name} onChange={handleChange} required className="p-1.5 border rounded-md w-full" />
                    </div>
                    <div>
                        <label htmlFor="case_number" className="block text-sm font-medium text-gray-700 mb-1">Case Number</label>
                        <input type="text" id="case_number" name="case_number" value={formData.case_number} onChange={handleChange} className="p-1.5 border rounded-md w-full" />
                    </div>
                    <div>
                        <label htmlFor="state_or_province" className="block text-sm font-medium text-gray-700 mb-1">State / Province *</label>
                        <input type="text" id="state_or_province" name="state_or_province" value={formData.state_or_province} onChange={handleChange} required className="p-1.5 border rounded-md w-full" />
                    </div>
                    <div>
                        <label htmlFor="county_or_region" className="block text-sm font-medium text-gray-700 mb-1">County / Region *</label>
                        <input type="text" id="county_or_region" name="county_or_region" value={formData.county_or_region} onChange={handleChange} required className="p-1.5 border rounded-md w-full" />
                    </div>

                    <div>
                        <label htmlFor="court_jurisdiction" className="block text-sm font-medium text-gray-700 mb-1">Court / Jurisdiction *</label>
                        <select id="court_jurisdiction" name="court_jurisdiction" value={formData.court_jurisdiction} onChange={handleChange} required className="p-1.5 border rounded-md bg-gray-50 w-full">
                            <option value="">Select...</option>
                            {COURT_JURISDICTIONS.map(j => <option key={j} value={j}>{j}</option>)}
                        </select>
                    </div>

                    <div>
                        <label htmlFor="service_type" className="block text-sm font-medium text-gray-700 mb-1">Type of Service *</label>
                        <select id="service_type" name="service_type" value={formData.service_type} onChange={handleChange} required className="p-1.5 border rounded-md bg-gray-50 w-full">
                            <option value="">Select...</option>
                            {SERVICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>

                    <div>
                        <label htmlFor="client_reference_number" className="block text-sm font-medium text-gray-700 mb-1">Client Reference #</label>
                        <input type="text" id="client_reference_number" name="client_reference_number" value={formData.client_reference_number} onChange={handleChange} className="p-1.5 border rounded-md w-full" />
                    </div>
                    {!isEditMode && (
                            <div>
                                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                <select id="status" name="status" value={formData.status} onChange={handleChange} className="p-1.5 border rounded-md bg-gray-50 w-full">
                                    {caseStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                    )}
                    <div>
                        <label htmlFor="due_date" className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                        <input type="date" id="due_date" name="due_date" value={formData.due_date} onChange={handleChange} className="p-1.5 border rounded-md w-full" />
                    </div>
                    <div className="col-span-full">
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Internal Case Description</label>
                        <textarea id="description" name="description" value={formData.description} onChange={handleChange} rows="3" className="p-1.5 border rounded-md w-full" />
                    </div>
                </FormSection>




                {/* 3. Servee Information (MODIFIED BLOCK) */}
                <div className="border border-gray-200 rounded-xl p-6 bg-white space-y-4">
                    <h2 className="text-xl font-extrabold text-blue-800 flex items-center space-x-2 border-b pb-2">
                        <BiMap className="w-6 h-6" />
                        <span>3. Servee Information</span>
                    </h2>
                    <div className="space-y-6">
                        {serveeData.map((servee, index) => (
                            <ServeeFormBlock
                                key={servee.id || index}
                                servee={servee}
                                index={index}
                                handleServeeChange={handleServeeChange}
                                handleRemoveServee={handleRemoveServee}
                                handleServeeAddFile={handleServeeAddFile}
                                handleServeeRemoveFile={handleServeeRemoveFile}
                                canRemove={serveeData.length > 1}
                                isServeeNew={!servee.id}
                            />
                        ))}
                    </div>
                    <button type="button" onClick={() => setServeeData(prev => [...prev, newServeeTemplate])}
                        className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg shadow-md hover:bg-green-600 transition duration-300">
                        <BiPlus className="w-5 h-5 mr-1" /> Add Another Servee
                    </button>
                </div>

                {/* 4. Documents & Instructions (MODIFIED BLOCK) */}
                <div className="border border-gray-200 rounded-xl p-6 bg-white space-y-6">
                    <h2 className="text-xl font-extrabold text-blue-800 flex items-center space-x-2 border-b pb-2">
                        <BiFile className="w-6 h-6" />
                        <span>4. Documents & Instructions</span>
                    </h2>

                    {/* Documents to be Served (Multi-select) */}
                    <div className="col-span-full">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Documents to be Served (Multi-select)</label>
                        <div className="flex flex-wrap gap-3">
                            {DOCUMENT_TYPES_TO_BE_SERVED.map(doc => (
                                <label key={doc} className="flex items-center space-x-1 border border-gray-300 px-3 py-1 rounded-full cursor-pointer hover:bg-gray-100 transition duration-150">
                                    <input
                                        type="checkbox"
                                        value={doc}
                                        checked={formData.documents_to_be_served.includes(doc)}
                                        onChange={handleDocumentTypeChange}
                                        className="form-checkbox text-blue-600 rounded"
                                    />
                                    <span className="text-sm">{doc}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Display Existing Case Files (Documents & Media) */}
                    {isEditMode && <ExistingCaseFiles files={caseFiles} handleRemoveExistingFile={handleRemoveExistingFile} />}

                    {/* Upload Case Files - ONE BY ONE WITH TITLE (New Files) */}
                    <div className="space-y-4 border border-blue-100 p-4 rounded-lg bg-blue-50">
                        <h3 className="text-lg font-bold text-blue-700">Upload New Case Documents</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2">
                                <label htmlFor="file_title" className="block text-sm font-medium text-gray-700 mb-1">Document Title *</label>
                                <input
                                    type="text"
                                    id="file_title"
                                    name="file_title"
                                    placeholder="e.g., Complaint and Summons"
                                    value={currentFileUpload.title}
                                    onChange={handleTitleChangeTemp}
                                    required={currentFileUpload.file !== null}
                                    className="p-1.5 border rounded-md w-full"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">File Upload *</label>
                                <input
                                    type="file"
                                    id="case_file_upload"
                                    onChange={handleFileUploadTemp}
                                    accept={ALLOWED_SERVE_FILE_TYPES}
                                    className="block w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 cursor-pointer p-1.5"
                                />
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={handleAddFile}
                            disabled={!currentFileUpload.file || currentFileUpload.title.trim() === ''}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition duration-300 disabled:opacity-50"
                        >
                            <BiPlus className="w-5 h-5 mr-1" /> Add File to Case
                        </button>
                    </div>

                    {/* Display NEW Files to be Uploaded */}
                    {caseFiles.filter(f => f.isNew).length > 0 && (
                        <div className="col-span-full">
                            <label className="block text-sm font-bold text-gray-700 mb-2">New Files to be Uploaded ({caseFiles.filter(f => f.isNew).length}):</label>
                            <ul className="text-sm text-gray-700 space-y-2">
                                {caseFiles.filter(f => f.isNew).map((item, index) => (
                                    <li key={index} className="flex justify-between items-center bg-gray-50 p-2 rounded-md border border-gray-200">
                                        <div className="truncate pr-4">
                                            <span className="font-semibold">{item.title}</span>
                                            <span className="text-gray-500 ml-2">({item.file.name})</span>
                                            <span className="text-xs text-gray-400 ml-2">({(item.file.size / 1024).toFixed(1)} KB)</span>
                                        </div>
                                        <button type="button" onClick={() => handleRemoveNewFile(index)} className="text-red-500 hover:text-red-700 flex-shrink-0">
                                            <BiTrash className="w-5 h-5" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}


                    {/* Number of Pages, Service Instructions, Special Instructions */}
                    <div className="col-span-full md:col-span-1">
                        <label htmlFor="number_of_pages" className="block text-sm font-medium text-gray-700">Number of Pages</label>
                        <input type="number" id="number_of_pages" name="number_of_pages" value={formData.number_of_pages} onChange={handleChange} min="0" className="p-1.5 border rounded-md w-full" />
                    </div>
                    <div className="col-span-full">
                        <label htmlFor="service_instructions" className="block text-sm font-medium text-gray-700 mb-1">Service Instructions</label>
                        <textarea id="service_instructions" name="service_instructions" placeholder="'Personal only', 'Mail after attempts', etc." value={formData.service_instructions} onChange={handleChange} rows="3" className="p-1.5 border rounded-md w-full" />
                    </div>
                    <div className="col-span-full">
                        <label htmlFor="special_instructions" className="block text-sm font-medium text-gray-700 mb-1">Special Instructions</label>
                        <textarea id="special_instructions" name="special_instructions" placeholder="Gate code, timing, hazards, etc." value={formData.special_instructions} onChange={handleChange} rows="3" className="p-1.5 border rounded-md w-full" />
                    </div>
                </div>


                {/* Submit Button */}
                <div className="flex justify-end space-x-4 pt-6">
                    <button
                        type="button"
                        onClick={() => navigate('/cases')}
                        className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition duration-300"
                    >
                        <BiX className="inline-block w-5 h-5 mr-1" /> Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={submitStatus === 'submitting'}
                        className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition duration-300 disabled:opacity-50 flex items-center justify-center"
                    >
                        {submitStatus === 'submitting' ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Saving Case...
                            </>
                        ) : isSaved ? (
                            <>
                                <BiSave className="inline-block w-5 h-5 mr-1" /> Saved
                            </>
                        ) : (
                            <>
                                <BiSave className="inline-block w-5 h-5 mr-1" /> {id ? 'Update Case' : 'Create New Intake'}
                            </>
                        )}
                    </button>
                </div>

                {submitStatus === 'success' && (
                    <p className="mt-4 text-green-500 text-center font-semibold">Case saved successfully! </p>
                )}
                {submitStatus === 'error' && (
                    <p className="mt-4 text-red-500 text-center font-semibold">{error}</p>
                )}
            </form>
        </div>
    );
};

export default ClientCaseForm;